import { createListCollection } from "@ark-ui/react";
import React, { MutableRefObject, useMemo } from "react";
import { Button } from "@mui/material";
import { Field, Form, useForm } from "react-final-form";

import Select from "@/components/select/select";
import {
  Modal,
  ModalActions,
  ModalComponent,
  ModalContent,
  ModalTitle,
  TextField,
  showToast,
} from "@/features/shared/components";
import { Location } from "@/lib/api";
import {
  useCreateDashboardLocationMutation,
  useUpdateDashboardLocationMutation,
} from "@/features/admin/features/dashboards";
import { useAdminDashboard } from "@/features/admin/hooks";
import { useObservedLocationsQuery } from "@/features/shared/widgets/observations-map/hooks/queries";
import { useTranslation } from "@/lib/i18n";
import {
  resolveCoordinatesAfterLinkedStationChange,
  stationCoordinates,
} from "@/features/admin/features/dashboards/dialogs/sync-location-coordinates-from-station";

type FormValues = Location;

type Props = {
  initialValues?: FormValues;
  onClose?: () => void;
  isNewLocation?: boolean;
};

const EMPTY_PARAMETER_LOCATION_VALUE = "";

const LocationSettingsDialog: React.ForwardRefRenderFunction<ModalComponent, Props> = (
  { onClose, initialValues, isNewLocation = false },
  ref,
) => {
  const { t } = useTranslation();
  const createDashboardLocationMutation = useCreateDashboardLocationMutation();
  const updateDashboardLocationMutation = useUpdateDashboardLocationMutation();

  const dashboard = useAdminDashboard();

  const modalRef = ref as MutableRefObject<ModalComponent>;

  const handleClose = () => {
    modalRef.current?.close();
  };

  /*
    Ideally you would wnat to create two seperate modals for create and update, but to keep this simple
    we will just use one modal for both. If the logic gets more complex consider splitting this up.
  */
  const handleSubmit = (values: FormValues) => {
    if (isNewLocation) {
      createDashboardLocationMutation.mutate(
        {
          dashboardId: dashboard.id,
          body: values,
        },
        {
          onSuccess: () => {
            handleClose();
            showToast(t("admin.messages.location_created"));
          },
        },
      );
    } else {
      updateDashboardLocationMutation.mutate(
        {
          dashboardId: dashboard.id,
          locationId: String(initialValues?.id),
          body: values,
        },
        {
          onSuccess: () => {
            handleClose();
            showToast(t("admin.messages.location_updated"));
          },
        },
      );
    }
  };

  return (
    <Modal onClose={onClose} ref={modalRef}>
      <Form<FormValues>
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit }) => (
          <>
            <ModalTitle>{t("admin.dashboard_settings.location_settings.title")}</ModalTitle>

            <ModalContent>
              <LocationForm onSubmit={handleSubmit} />
            </ModalContent>
            <ModalActions>
              <Button key="cancel" onClick={handleClose}>
                {t("general.cancel")}
              </Button>
              <Button key="save" onClick={handleSubmit}>
                {t("general.save")}
              </Button>
            </ModalActions>
          </>
        )}
      />
    </Modal>
  );
};

const LocationForm = ({ onSubmit }: { onSubmit: () => void }) => {
  const { t } = useTranslation();
  const form = useForm<FormValues>();
  const translationKey = "admin.dashboard_settings.location_settings.fields";
  const parameterLocationsQuery = useObservedLocationsQuery();
  const parameterLocations = parameterLocationsQuery.data;

  const parameterLocationCollection = useMemo(() => {
    const items = [
      {
        label: t(`${translationKey}.parameter_location_id.empty_option`),
        value: EMPTY_PARAMETER_LOCATION_VALUE,
      },
      ...(parameterLocations ?? []).map((location) => ({
        label: location.name,
        value: location.id,
      })),
    ];
    return createListCollection({ items });
  }, [parameterLocations, t]);

  return (
    <form onSubmit={onSubmit} noValidate>
      <TextField autoFocus name="name" label={t(`${translationKey}.name.label`)} required />
      <TextField
        name="legacy_wind_prediction_name"
        label={t(`${translationKey}.windPredictionName.label`)}
      />
      <TextField name="kmds_code" label={t(`${translationKey}.kmds.label`)} />
      <TextField
        name="knmi_station_code"
        label={t(`${translationKey}.stationCode.label`)}
      />
      <Field<Location["parameter_location_id"]> name="parameter_location_id">
        {({ input }) => (
          <Select
            label={t(`${translationKey}.parameter_location_id.label`)}
            collection={parameterLocationCollection}
            value={input.value ?? EMPTY_PARAMETER_LOCATION_VALUE}
            onValueChange={(value) => {
              const nextId = value === EMPTY_PARAMETER_LOCATION_VALUE ? null : value;
              const previousId = input.value ?? null;
              const values = form.getState().values;
              const nextCoordinates = resolveCoordinatesAfterLinkedStationChange({
                currentLatitude: values.latitude,
                currentLongitude: values.longitude,
                previousStation: stationCoordinates(
                  parameterLocations?.find((station) => station.id === previousId),
                ),
                nextStation: stationCoordinates(
                  parameterLocations?.find((station) => station.id === nextId),
                ),
              });
              input.onChange(nextId);
              if (nextCoordinates) {
                form.change("latitude", nextCoordinates.latitude);
                form.change("longitude", nextCoordinates.longitude);
              }
            }}
            placeholder={t(`${translationKey}.parameter_location_id.hint`)}
            disabled={parameterLocationsQuery.isPending}
          />
        )}
      </Field>
      <TextField
        name="latitude"
        label={t(`${translationKey}.lat.label`)}
        type="number"
        fieldProps={{
          validate: (value, values) => {
            if ((values as FormValues)?.longitude !== undefined && value === undefined) {
              return t("form_validation.required");
            }
          },
        }}
      />
      <TextField
        name="longitude"
        label={t(`${translationKey}.lon.label`)}
        type="number"
        fieldProps={{
          validate: (value, values) => {
            if ((values as FormValues)?.latitude !== undefined && value === undefined) {
              return t("form_validation.required");
            }
          },
        }}
      />
    </form>
  );
};

export default React.forwardRef(LocationSettingsDialog);
